/*
 * ew-vehicle-resolver.js
 *
 * Anonymous-vehicle PERSISTENCE singleton + canonical suggest/format/resolve.
 *
 * Persistence (this file): the picked vehicle is stored in
 * localStorage['ew_active_vehicle'] with a 365-day SLIDING TTL sidecar
 * (ew_active_vehicle_ts). read/write/clear canonicalize chassis -> `code`
 * and engine -> `engine_code`, fire `ew:vehicle-resolved` on write and
 * `ew:vehicle-cleared` on clear, and boot() re-emits the resolved event so
 * the header pill paints from any already-persisted vehicle.
 *
 * Public API (window.EWVehicleResolver): read / write / clear, plus the
 * canonical shared suggest / formatSuggestion / resolveByCode /
 * resolveByCodeTrim backed by the static JSON dataset
 * (assets/ew-chassis-data.json). Homepage input, Garage dialog, the unified
 * picker, and the Tune Builder all consume this single matcher.
 *
 * GAR-1.7: the tabbed Vehicle Resolver MODAL was retired. Its controller
 * (the Resolver class + its island/data loader + modal-only utils) has been
 * removed from this file. The unified Garage dialog (ew-garage-dialog.js,
 * opened via [data-open-garage-dialog]) is now the single vehicle-picker UI.
 *
 * Vanilla JS. No dependencies.
 */
(function () {
  'use strict';

  var LS_KEY = 'ew_active_vehicle';
  /* Sidecar timestamp key — written alongside the vehicle value so the
     stored value shape stays backward-compatible (header.liquid + the
     garage dialog read the raw LS_KEY string and must not see a wrapper). */
  var LS_TS_KEY = 'ew_active_vehicle_ts';
  /* GAR-1.6 anonymous persistence TTL. Mirror constant in ew-livery.js
     (separate IIFE — keep both in sync). ~365 days as a sliding window:
     refreshed on every writeActive(), so an active returning user never
     expires; only a vehicle untouched for a year is dropped. */
  var TTL_MS = 365 * 24 * 60 * 60 * 1000;

  /* ---- Active vehicle storage (anonymous mode) ---- */
  function readActive() {
    var v;
    try { v = JSON.parse(localStorage.getItem(LS_KEY) || 'null'); } catch (_) { return null; }
    if (!v) return null;
    /* TTL check via the sidecar timestamp.
       - ts present + stale (> TTL_MS): expired — wipe BOTH keys, return null.
       - ts present + fresh: serve it.
       - ts MISSING (legacy value written before GAR-1.6): treat as fresh and
         stamp it now, so existing visitors aren't surprise-wiped. */
    try {
      var tsRaw = localStorage.getItem(LS_TS_KEY);
      if (tsRaw == null) {
        localStorage.setItem(LS_TS_KEY, String(Date.now()));
      } else {
        var ts = parseInt(tsRaw, 10);
        if (isFinite(ts) && (Date.now() - ts) > TTL_MS) {
          localStorage.removeItem(LS_KEY);
          localStorage.removeItem(LS_TS_KEY);
          return null;
        }
      }
    } catch (_) {}
    /* F-05 — year provenance on READ. A legacy value (year present, no
       year_known:true) carries the old band-end FABRICATION ("2025" for a
       2019–2025 G20) styled as a model year. Normalize it away in the
       returned object so every consumer reads it as year-UNKNOWN; the
       honest ys–ye band (when present) is untouched. Storage itself is
       rewritten only on the next writeActive() — keys are not bumped. */
    if (v && typeof v === 'object' && v.year_known !== true && v.year != null) {
      try { delete v.year; } catch (_) {}
    }
    return v;
  }
  function writeActive(v) {
    /* CHASSIS-NAV-1 (P1c-2) — canonicalize the chassis code to `code` at the
       single write funnel. Migrate-on-read from every legacy field name a
       writer or a previously-persisted value may carry (code / chassis_code /
       chassis), then set ONLY the canonical `v.code`.

       The chassis_code / chassis write-aliases were DROPPED in P1c-2: every
       reader of the persisted vehicle object (header pill, my-garage /
       my-builds / main-account, the configure-* islands, shop-by-vehicle,
       tuning-stage-hub, garage-dialog, fitment-filter, use-case-prompt,
       configurator, tune-builder) now reads code-first with a
       (|| chassis_code || chassis) migrate-on-read fallback, so existing
       saved garages keep resolving while new writes are single-keyed.
       Engine is canonicalized the same way to the single `engine_code`. */
    if (v && typeof v === 'object') {
      var cc = v.code || v.chassis_code || v.chassis;
      var ec = v.engine_code  || v.engine;
      if (cc) {
        v.code = cc;
        try { delete v.chassis_code; } catch (_) {}
        try { delete v.chassis; } catch (_) {}
      }
      if (ec) {
        v.engine_code = ec;
        try { delete v.engine; } catch (_) {}
      }
      /* F-05 — year provenance at the single WRITE funnel. A model year is
         persisted ONLY with provenance (year_known === true, set by a writer
         that captured a user-supplied year validated against the row's
         ys–ye band). Anything else — a band-end default, a legacy carry-over,
         junk — is deleted: a year without provenance is the fabrication this
         contract kills. ys/ye (the honest band) always pass through. */
      if (v.year_known === true) {
        var yk = typeof v.year === 'number' ? v.year : parseInt(v.year, 10);
        if (isFinite(yk)) {
          v.year = yk;
        } else {
          try { delete v.year; } catch (_) {}
          try { delete v.year_known; } catch (_) {}
        }
      } else {
        try { delete v.year; } catch (_) {}
        try { delete v.year_known; } catch (_) {}
      }
    }
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(v));
      /* Refresh the sliding-window timestamp on every write. */
      localStorage.setItem(LS_TS_KEY, String(Date.now()));
    } catch (_) {}
    document.dispatchEvent(new CustomEvent('ew:vehicle-resolved', { detail: v }));
  }
  function clearActive() {
    try {
      localStorage.removeItem(LS_KEY);
      localStorage.removeItem(LS_TS_KEY);
    } catch (_) {}
    document.dispatchEvent(new CustomEvent('ew:vehicle-cleared'));
  }

  /* ============================================================
     Boot
     ============================================================ */
  function boot() {
    /* Render current vehicle on pill if one is already in localStorage.
       Pill paint is driven by the ew:vehicle-resolved event handled in
       sections/header.liquid. */
    var active = readActive();
    if (active) {
      document.dispatchEvent(new CustomEvent('ew:vehicle-resolved', { detail: active }));
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* ============================================================
     CANONICAL SUGGEST / FORMAT / RESOLVE
     ------------------------------------------------------------
     The single matcher for every surface. Ported VERBATIM from the
     homepage autocomplete (assets/ew-homepage-vehicle-input.js) so the
     typeahead experience is byte-identical everywhere. Backed by the
     static JSON dataset (assets/ew-chassis-data.json) loaded ONCE here.
     ============================================================ */

  /* ---- One-time JSON dataset loader ---- */
  var _suggestData = [];
  var _suggestLoaded = false;
  var _suggestPromise = null;

  /* Robust global source for the dataset URL: the always-global
     vehicle-data-island element carries data-asset-url; fall back to the
     dialog / homepage-input widgets if a page renders those instead. */
  function suggestAssetUrl() {
    var el = document.querySelector('[data-ew-chassis-data][data-asset-url]') ||
             document.querySelector('[data-ew-garage-dialog][data-asset-url]') ||
             document.querySelector('[data-ew-hvi][data-asset-url]');
    return el ? el.getAttribute('data-asset-url') : '';
  }

  function loadSuggestData() {
    if (_suggestLoaded) return Promise.resolve();
    if (_suggestPromise) return _suggestPromise;
    var url = suggestAssetUrl();
    if (!url) { _suggestData = []; _suggestLoaded = true; return Promise.resolve(); }
    _suggestPromise = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (json) { _suggestData = Array.isArray(json) ? json : []; _suggestLoaded = true; })
      .catch(function () { _suggestData = []; _suggestLoaded = true; });
    return _suggestPromise;
  }

  /* Levenshtein distance, capped at 2 for early-exit speed.
     (VERBATIM from ew-homepage-vehicle-input.js) */
  function levSuggest(a, b) {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;
    var prev = [];
    for (var j = 0; j <= b.length; j++) prev[j] = j;
    for (var i = 1; i <= a.length; i++) {
      var curr = [i];
      for (var k = 1; k <= b.length; k++) {
        var cost = a.charAt(i - 1) === b.charAt(k - 1) ? 0 : 1;
        curr[k] = Math.min(curr[k - 1] + 1, prev[k] + 1, prev[k - 1] + cost);
      }
      prev = curr;
      if (Math.min.apply(null, curr) > 2) return 3;
    }
    return prev[b.length];
  }

  /* Field-specificity weights — higher = more discriminating.
     (VERBATIM from ew-homepage-vehicle-input.js) */
  var SUGGEST_WEIGHT = {
    year: 12,
    engine: 10,
    trim: 10,
    code: 9,
    model: 6,
    make: 2,
  };

  function suggestTokenMatches(token, item) {
    token = token.toLowerCase();
    var hits = [];
    var year = parseInt(token, 10);
    if (!isNaN(year) && year >= 2000 && year <= 2030) {
      var ys = parseInt(item.ys, 10);
      var ye = parseInt(item.ye, 10);
      if (ys && ye && year >= ys && year <= ye) hits.push({ field: 'year', weight: SUGGEST_WEIGHT.year });
    }
    var code = (item.code || '').toLowerCase();
    var engine = (item.engine || '').toLowerCase();
    var make = (item.make || '').toLowerCase();
    var model = (item.model || '').toLowerCase();
    var trim = (item.trim || '').toLowerCase();

    function fuzzy(field, value, weight) {
      if (!value) return;
      if (value.indexOf(token) !== -1) { hits.push({ field: field, weight: weight }); return; }
      if (token.length >= 4 && levSuggest(token, value) <= 1) {
        hits.push({ field: field, weight: weight * 0.7 });
      }
    }
    fuzzy('code', code, SUGGEST_WEIGHT.code);
    fuzzy('engine', engine, SUGGEST_WEIGHT.engine);
    fuzzy('make', make, SUGGEST_WEIGHT.make);
    fuzzy('model', model, SUGGEST_WEIGHT.model);
    fuzzy('trim', trim, SUGGEST_WEIGHT.trim);
    return hits;
  }

  function scoreItem(query, item) {
    var tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
    var totalScore = 0;
    var matchedTokens = 0;
    for (var i = 0; i < tokens.length; i++) {
      var hits = suggestTokenMatches(tokens[i], item);
      if (hits.length) {
        matchedTokens++;
        var best = hits.reduce(function (a, b) { return a.weight > b.weight ? a : b; });
        totalScore += best.weight;
      }
    }
    if (matchedTokens === tokens.length && tokens.length > 1) totalScore *= 1.4;
    return { score: totalScore, matched: matchedTokens, total: tokens.length };
  }

  var SUGGEST_MAX_VISIBLE = 6;

  /* Canonical suggest(): returns up to 6 model-level item objects, scored +
     sorted + deduped EXACTLY as the homepage autocomplete. */
  function suggest(query) {
    query = query || '';
    if (!query.trim()) return [];
    var scored = _suggestData.map(function (it) {
      var s = scoreItem(query, it);
      return { item: it, score: s.score };
    }).filter(function (r) { return r.score > 0; });

    scored.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      var ay = parseInt(a.item.ye, 10) || 0;
      var by = parseInt(b.item.ye, 10) || 0;
      return by - ay;
    });

    /* R2 2.4 — dedup by `handle`, which is unique per chassis metaobject.
       ⛔ This key MOVED WITH THE DATA and must not be reverted independently.
       The old key was (code + '|' + trim). That worked only while trim
       multiplied rows: one row per (chassis x model x trim). ew-chassis-data.json
       is now one row per metaobject, so under the old key F10 528i, 535i and
       550i all collapse to "F10|" — three different cars, one survivor in the
       dropdown, and no error anywhere. The fallback covers a legacy asset that
       predates `handle`. */
    var seen = {};
    var items = [];
    for (var i = 0; i < scored.length; i++) {
      var _it = scored[i].item;
      var key = _it.handle ||
                (_it.code + '|' + (_it.model || '') + '|' + (_it.engine_code || _it.trim || ''));
      if (seen[key]) continue;
      seen[key] = true;
      items.push(scored[i].item);
    }
    return items.slice(0, SUGGEST_MAX_VISIBLE);
  }

  /* R2 2.1 — engine-family normalisation lives in ONE place:
     assets/ew-engine-family.js (loaded un-deferred in layout/theme.liquid,
     above every consumer). This module keeps exporting `engineFamily` because
     ~10 surfaces already call EWVehicleResolver.engineFamily(); that export is
     now a reference to the shared function, not a second implementation.
     NEVER mutate the stored engine_code used for matching/tune resolution. */
  var engineFamily = window.EWEngineFamily;

  /* Canonical formatSuggestion(): {line, meta} EXACTLY as the homepage
     fmtSuggestion. */
  function formatSuggestion(item) {
    var yr = '';
    if (item.ys && item.ye) yr = item.ys + '–' + item.ye;
    var parts = [yr, item.make || '', item.model || ''].filter(Boolean);
    var line = parts.join(' ');
    /* R2 2.4 — trim is NO LONGER appended to the line. Under the old grain
       (one row per chassis x model x trim) trim was what told two rows apart,
       so it had to show. Rows are now one per metaobject, disambiguated by
       model + engine, and `trim` holds the whole list ("Base · M Sport ·
       xDrive M Sport") — appending it would make every line a paragraph.
       It stays searchable: suggestTokenMatches still fuzzy-matches on it, so
       typing "Competition" still finds the car. */
    var fam = engineFamily(item.engine);
    var meta = (item.code || '') + (fam ? ' · ' + fam : '');
    return { line: line, meta: meta };
  }

  /* Resolve helpers for staging from a code (and optional trim). Return the
     raw dataset record (or null). */
  function resolveByCode(code) {
    if (!code) return null;
    code = String(code).toUpperCase();
    for (var i = 0; i < _suggestData.length; i++) {
      if ((_suggestData[i].code || '').toUpperCase() === code) return _suggestData[i];
    }
    return null;
  }
  /* F-05 — code+trim is NOT unique in the dataset (G20 carries both
     "330i · Base" and "M340i · Base"), and the suggest() dedup key
     (code|trim) hides the collision in the dropdown — so a trim-only
     resolve could stage the WRONG model (wrong engine, wrong ys–ye band).
     The optional `model` arg disambiguates: prefer model+trim, then model,
     then trim, then the first record for the code. Called without `model`,
     behavior is byte-identical to the previous signature. */
  function resolveByCodeTrim(code, trim, model) {
    if (!code) return null;
    code = String(code).toUpperCase();
    trim = (trim || '').toLowerCase();
    model = (model || '').toLowerCase();
    var firstForCode = null, modelTrim = null, modelOnly = null, trimOnly = null;
    for (var i = 0; i < _suggestData.length; i++) {
      var it = _suggestData[i];
      if ((it.code || '').toUpperCase() !== code) continue;
      if (!firstForCode) firstForCode = it;
      var mOk = model && (it.model || '').toLowerCase() === model;
      var tOk = (it.trim || '').toLowerCase() === trim;
      if (mOk && tOk) { modelTrim = modelTrim || it; }
      if (mOk) { modelOnly = modelOnly || it; }
      if (tOk) { trimOnly = trimOnly || it; }
    }
    return modelTrim || modelOnly || trimOnly || firstForCode;
  }

  /* ============================================================
     F-05 — MODEL-YEAR PROVENANCE + THE canonical display formatter.
     One implementation; every surface (garage dialog, homepage input,
     tune builder, fitment bar, shop-by-vehicle, fast lanes) consumes it.
     Rule (GATE1-1 F-05, locked):
       year KNOWN (user-typed, validated inside the row's ys–ye band)
         → "2021 BMW 330i (G20 · B48)"
       year UNKNOWN → the honest band "2019–2025 G20 330i"
         — NEVER the band-end styled as a model year.
     ============================================================ */

  /* yearFromQuery(query, row) — tokenize the typed query; an integer token
     2000–2030 that sits inside parseInt(row.ys)–parseInt(row.ye) is the
     user's model year (returned as a number). Anything else → null. */
  function yearFromQuery(query, row) {
    if (!query || !row) return null;
    var ys = parseInt(row.ys, 10);
    var ye = parseInt(row.ye, 10);
    if (!ys || !ye) return null;
    var tokens = String(query).trim().split(/\s+/);
    for (var i = 0; i < tokens.length; i++) {
      if (!/^\d{4}$/.test(tokens[i])) continue;
      var y = parseInt(tokens[i], 10);
      if (y < 2000 || y > 2030) continue;
      if (y >= ys && y <= ye) return y;
    }
    return null;
  }

  /* A vehicle is year-known ONLY with provenance: year_known === true AND a
     numeric year. Legacy persisted values (year present, no year_known) read
     as year-UNKNOWN — their year was the fabricated band end. */
  function isYearKnown(v) {
    return !!(v && v.year_known === true && typeof v.year === 'number' && isFinite(v.year));
  }

  /* Honest ys–ye band for a vehicle: prefer the value's own ys/ye; recover
     from the loaded dataset via resolveByCodeTrim when missing. Returns ''
     when no band is recoverable (dataset not loaded / no row) — callers
     fall back to a NO-year form, never an invented one. */
  function bandOf(v) {
    var ys = v.ys, ye = v.ye;
    if (!(ys && ye)) {
      var row = resolveByCodeTrim(v.code || v.chassis_code || v.chassis, v.trim || '', v.model || '');
      if (row && row.ys && row.ye) { ys = row.ys; ye = row.ye; }
    }
    return (ys && ye) ? String(ys) + '–' + String(ye) : '';
  }

  /* displayLabel(v) — THE canonical persisted-vehicle display string. */
  function displayLabel(v) {
    if (!v) return '';
    var code = String(v.code || v.chassis_code || v.chassis || '').toUpperCase();
    var fam = engineFamily(v.engine_code || v.engine || '');
    var make = v.make || '';
    var model = v.model || '';
    if (isYearKnown(v)) {
      var head = [v.year, make, model].filter(Boolean).join(' ');
      var meta = [code, fam].filter(Boolean).join(' · ');
      if (!meta) return head;
      return head ? head + ' (' + meta + ')' : meta;
    }
    var band = bandOf(v);
    if (band) return [band, code, model].filter(Boolean).join(' ');
    /* No band recoverable — make/model + code, NO year, never invented. */
    var noYear = [make, model].filter(Boolean).join(' ');
    if (!code) return noYear;
    return noYear ? noYear + ' (' + code + ')' : code;
  }

  /* cartVehicleProperty(v) — the cart/order "Vehicle" line-property value
     (FUL-1's packing slip reads the 'Vehicle' key; the VALUE follows the
     same provenance rule as the display label). */
  function cartVehicleProperty(v) {
    if (!v) return '';
    var code = String(v.code || v.chassis_code || v.chassis || '').toUpperCase();
    var fam = engineFamily(v.engine_code || v.engine || '');
    var make = v.make || '';
    var model = v.model || '';
    if (isYearKnown(v)) {
      var head = [v.year, make, model].filter(Boolean).join(' ');
      return [head, code, fam].filter(Boolean).join(' · ');
    }
    var band = bandOf(v);
    if (band) {
      var bandHead = [band, code, model].filter(Boolean).join(' ');
      return [bandHead, fam].filter(Boolean).join(' · ');
    }
    var noYear = [make, model].filter(Boolean).join(' ');
    if (code) noYear = noYear ? noYear + ' (' + code + ')' : code;
    return [noYear, fam].filter(Boolean).join(' · ');
  }

  /* Public API */
  window.EWVehicleResolver = {
    read: readActive,
    write: writeActive,
    clear: clearActive,
    /* Canonical shared suggest/format/resolve (BUGS-1 Bug 3 unification) */
    ready: loadSuggestData,
    data: function () { return _suggestData; },
    suggest: suggest,
    formatSuggestion: formatSuggestion,
    /* AESTHETIC-1 CFG-2 — shared engine-family shortener for DISPLAY labels
       (header/mobile pill, shop-by-vehicle H1, my-garage card). Single source
       of truth so all label surfaces shorten the engine code identically. */
    engineFamily: engineFamily,
    resolveByCode: resolveByCode,
    resolveByCodeTrim: resolveByCodeTrim,
    /* F-05 — model-year provenance + canonical persisted-vehicle formatting.
       yearFromQuery: parse a validated user year out of a typed query;
       displayLabel / cartVehicleProperty: THE display + cart-property
       formatters (year only with provenance; honest ys–ye band otherwise). */
    yearFromQuery: yearFromQuery,
    displayLabel: displayLabel,
    cartVehicleProperty: cartVehicleProperty,
  };
})();
